/**
 * Meta Pixel runtime monitor.
 *
 * Monkey-patches `window.fbq` para interceptar TODO evento que se dispare
 * (PageView automático, ViewContent, InitiateCheckout, Purchase, Lead, etc.),
 * incluyendo los que vienen de los 3 wrappers y los que llaman a fbq
 * directamente.
 *
 * Expone:
 *   - `window.__metaPixelLog__`     → array circular de últimos 50 events
 *   - `window.__metaPixelStatus__()`→ helper de diagnóstico instantáneo
 *
 * Activación: `setupMetaPixelMonitor()` se llama una vez en `main.tsx`.
 * El monitor no rompe el flujo del Pixel original — todos los args se
 * pasan a la función original sin modificar.
 *
 * Para inspeccionar en producción:
 *   ▸ Open DevTools Console
 *   ▸ Type `__metaPixelStatus__()` → snapshot del estado actual
 *   ▸ Type `__metaPixelLog__`      → ver últimos 50 events
 */

const MAX_LOG_SIZE = 50;

export type PixelLogEntry = {
  timestamp: number;
  /** ISO string for human-readable inspection in DevTools */
  iso: string;
  command: string;
  eventName: string;
  params?: Record<string, unknown>;
  options?: { eventID?: string };
};

export type PixelStatus = {
  pixelId: string | null;
  fbqLoaded: boolean;
  fbqVersion: string | null;
  eventsFired: number;
  eventsByName: Record<string, number>;
  lastEvent: PixelLogEntry | null;
  testEventsManagerUrl: string | null;
};

declare global {
  interface Window {
    __metaPixelLog__?: PixelLogEntry[];
    __metaPixelStatus__?: () => PixelStatus;
    __metaPixelMonitorAttached__?: boolean;
  }
}

/**
 * Permissive shape of `window.fbq` as it actually exists at runtime — the
 * Meta loader attaches `version`, `loaded`, `queue`, `callMethod` to the
 * function instance. Other modules declare narrower call signatures; we
 * use this shape internally and cast at the boundary.
 */
type FbqRuntime = ((command: string, ...args: unknown[]) => void) & {
  version?: string;
  callMethod?: unknown;
  queue?: unknown[];
  loaded?: boolean;
};

const PIXEL_ID_FROM_INIT_REGEX = /^\d{15,16}$/;

let installedPixelId: string | null = null;

function pushEntry(entry: PixelLogEntry) {
  if (typeof window === "undefined") return;
  if (!Array.isArray(window.__metaPixelLog__)) window.__metaPixelLog__ = [];
  window.__metaPixelLog__.push(entry);
  // Cap the buffer — keep the most recent N
  if (window.__metaPixelLog__.length > MAX_LOG_SIZE) {
    window.__metaPixelLog__ = window.__metaPixelLog__.slice(-MAX_LOG_SIZE);
  }
}

function snapshotStatus(): PixelStatus {
  const log = (typeof window !== "undefined" && window.__metaPixelLog__) || [];
  const byName: Record<string, number> = {};
  for (const e of log) {
    byName[e.eventName] = (byName[e.eventName] ?? 0) + 1;
  }
  const fbq =
    typeof window !== "undefined"
      ? (window.fbq as unknown as FbqRuntime | undefined)
      : undefined;
  return {
    pixelId: installedPixelId,
    fbqLoaded: typeof fbq === "function" && fbq.loaded === true,
    fbqVersion: fbq?.version ?? null,
    eventsFired: log.length,
    eventsByName: byName,
    lastEvent: log.length > 0 ? log[log.length - 1] : null,
    testEventsManagerUrl: installedPixelId
      ? `https://business.facebook.com/events_manager2/list/pixel/${installedPixelId}/test_events`
      : null,
  };
}

/**
 * Set up the monitor. Idempotent — safe to call multiple times.
 * Should be called early in app startup (before any tracking fires) so
 * the first auto-PageView is captured.
 */
export function setupMetaPixelMonitor(opts?: { pixelId?: string }): void {
  if (typeof window === "undefined") return;
  if (window.__metaPixelMonitorAttached__) return;
  window.__metaPixelMonitorAttached__ = true;

  installedPixelId = opts?.pixelId ?? null;

  // Initialize log + status helper on window so DevTools can read them.
  if (!Array.isArray(window.__metaPixelLog__)) window.__metaPixelLog__ = [];
  window.__metaPixelStatus__ = snapshotStatus;

  const attachInterceptor = () => {
    const originalFbq = window.fbq as unknown as FbqRuntime | undefined;
    if (typeof originalFbq !== "function") return false;

    // Try to derive pixelId from the queue when possible (init call).
    if (!installedPixelId && Array.isArray(originalFbq.queue)) {
      for (const args of originalFbq.queue) {
        if (
          Array.isArray(args) &&
          args[0] === "init" &&
          typeof args[1] === "string" &&
          PIXEL_ID_FROM_INIT_REGEX.test(args[1])
        ) {
          installedPixelId = args[1];
          break;
        }
      }
    }

    const wrapped: FbqRuntime = function (this: unknown, command: string, ...args: unknown[]) {
      try {
        // Capture init too so we know the active pixel id at runtime.
        if (command === "init" && typeof args[0] === "string") {
          if (PIXEL_ID_FROM_INIT_REGEX.test(args[0])) {
            installedPixelId = args[0];
          }
        }

        const isTrack = command === "track" || command === "trackCustom";
        if (isTrack) {
          const eventName = typeof args[0] === "string" ? args[0] : "(unknown)";
          const params = (args[1] ?? undefined) as Record<string, unknown> | undefined;
          const options = (args[2] ?? undefined) as { eventID?: string } | undefined;
          const now = Date.now();
          pushEntry({
            timestamp: now,
            iso: new Date(now).toISOString(),
            command,
            eventName,
            params,
            options,
          });

          if (import.meta.env.DEV) {
            // Console log in dev so devs see events immediately.
            // In prod we stay silent — events still go to window.__metaPixelLog__.
            // eslint-disable-next-line no-console
            console.log(
              `[meta-pixel] ${command} ${eventName}`,
              params ?? {},
              options ? `(eventID=${options.eventID})` : "",
            );
          }
        }
      } catch {
        // never break tracking
      }
      // Forward to original — preserves Pixel behavior exactly.
      return (originalFbq as (...a: unknown[]) => unknown).call(
        this,
        command,
        ...args,
      );
    };

    // Preserve all properties Meta's loader attaches.
    wrapped.callMethod = originalFbq.callMethod;
    wrapped.queue = originalFbq.queue;
    wrapped.version = originalFbq.version;
    wrapped.loaded = originalFbq.loaded;
    Object.setPrototypeOf(wrapped, Object.getPrototypeOf(originalFbq));

    // Cast through unknown to bridge to the narrower types declared by the
    // wrappers in src/lib/metaCapi.ts and src/lib/metaPixelAnalytics.ts.
    window.fbq = wrapped as unknown as typeof window.fbq;
    return true;
  };

  // The Pixel loader may not have run yet (async script). Poll briefly,
  // then give up gracefully — events fired before attach won't be logged
  // but everything after will.
  if (!attachInterceptor()) {
    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts++;
      if (attachInterceptor() || attempts > 50) {
        window.clearInterval(interval);
      }
    }, 100); // 50 * 100ms = 5s budget
  }
}
